// tracciato.js v3 - 22/09/2026
//   v3: formatoIt(), per MOSTRARE i numeri all'italiana (1.234,50)
//   v2: registro degli input e ri-decorazione quando arrivano le regole
//
// Rende i campi dei form COERENTI COL TRACCIATO AS400.
//
// Le regole (tipo, lunghezza, decimali) arrivano da file_as400_values, la stessa
// tabella che usa il codice che scrive i file: non ci sono numeri ricopiati nei
// template, quindi form e tracciato non possono divergere.
//
// Uso nel template, una riga per campo:
//
//     <input v-tracciato="'TXPES'" v-model="dest.TXPES">
//
// Funziona anche messa su un componente: la direttiva cerca l'<input> dentro
// l'elemento radice.
//
// Cosa impedisce di digitare:
//   campo S (numerico), decimali 0  -> solo cifre, al massimo <lunghezza> cifre
//   campo S con decimali            -> cifre e UNA virgola, con al massimo
//                                      <intere> cifre intere e <decimali> decimali.
//                                      Mai il segno meno, mai lettere.
//   campo A (testo)                 -> al massimo <lunghezza> caratteri, contati
//                                      su quello che finira' nel file (dopo la
//                                      riduzione ad ASCII) e senza a capo.
//
// A VIDEO la virgola, AL SERVER il punto: vedi valoriPerTracciato() in fondo.
// Il valore che parte nel POST resta identico a quello che parte oggi, quindi
// questo file non dipende dalla patch a compose() e non cambia niente di cio' che
// finisce in tabella.
//
// --- differenze rispetto alla v1 -------------------------------------------
// La v1 applicava la direttiva solo in 'inserted' e 'componentUpdated'. Le regole
// pero' arrivano DOPO (chiamata HTTP), e siccome nessun template le legge il
// componente non si ri-disegna: su un form che resta fermo la decorazione non
// sarebbe mai stata applicata. Adesso ogni input marcato viene registrato e
// ri-decorato quando le regole arrivano. Il filtro sulla digitazione, invece,
// legge la regola nel momento in cui l'utente scrive, quindi funziona comunque.

import Vue from 'vue';
import axios from 'axios';

const stato = Vue.observable({ regole: {}, caricato: {} });

// input marcati con v-tracciato -> nome del campo. Serve a ri-decorarli quando le
// regole arrivano. E' una Map e non un array perche' gli input vanno e vengono
// (destinatari aggiunti e tolti) e unbind deve poterli togliere.
const registrati = new Map();

const MAPPA = {
    'À':'A','Á':'A','Â':'A','Ã':'A','Ä':'A','Å':'A','Æ':'AE',
    'à':'a','á':'a','â':'a','ã':'a','ä':'a','å':'a','æ':'ae',
    'È':'E','É':'E','Ê':'E','Ë':'E','è':'e','é':'e','ê':'e','ë':'e',
    'Ì':'I','Í':'I','Î':'I','Ï':'I','ì':'i','í':'i','î':'i','ï':'i',
    'Ò':'O','Ó':'O','Ô':'O','Õ':'O','Ö':'O','Ø':'O',
    'ò':'o','ó':'o','ô':'o','õ':'o','ö':'o','ø':'o',
    'Ù':'U','Ú':'U','Û':'U','Ü':'U','ù':'u','ú':'u','û':'u','ü':'u',
    'Ç':'C','ç':'c','Ñ':'N','ñ':'n','Ý':'Y','ý':'y','ÿ':'y',
    'ß':'SS','Œ':'OE','œ':'oe','Š':'S','š':'s','Ž':'Z','ž':'z',
    'Ð':'D','ð':'d','Þ':'TH','þ':'th',
    '‘':"'", '’':"'", '‚':"'", '‛':"'",
    '“':'"', '”':'"', '„':'"', '‟':'"',
    '–':'-', '—':'-', '−':'-', '‑':'-',
    '…':'...', '•':'-', '·':'.', '°':' ',
    '€':'EUR', '£':'GBP', ' ':' '
};

/** Stessa riduzione che applica il server quando scrive il file. */
export function adAscii(t) {
    let s = String(t == null ? '' : t);
    s = s.replace(/[ °·£À-ÿŒœŠšŽž‑–—‘-‟•…−€]/g,
        c => (MAPPA[c] !== undefined ? MAPPA[c] : c));
    // eslint-disable-next-line no-control-regex
    return s.replace(/[^\x20-\x7E]/g, ' ');
}

/** Carica le regole di un tracciato una volta sola. */
export async function caricaRegole(uri, tracciato) {
    if (stato.caricato[tracciato]) return stato.regole;
    stato.caricato[tracciato] = true;
    try {
        const res = await axios.post(uri + '/tracciato/regole', { tracciato: tracciato });
        const regole = res.data && res.data.value ? res.data.value : res.data;
        Object.keys(regole || {}).forEach(k => Vue.set(stato.regole, k, regole[k]));
        // le regole sono arrivate dopo che gli input erano gia' nel DOM: vanno
        // decorati adesso, altrimenti maxlength e suggerimento non compaiono mai
        decoraTutti();
    } catch (e) {
        // Senza regole i campi restano liberi come sono oggi: la pagina non si rompe,
        // ma il blocco non c'e'. Va in console perche' e' una condizione anomala.
        stato.caricato[tracciato] = false;
        console.error('tracciato: regole non caricate per ' + tracciato, e);
    }
    return stato.regole;
}

export function regolaDi(campo) {
    return stato.regole[campo];
}

// ---------------------------------------------------------------- filtri

function ripulisciTesto(v, lunghezza) {
    let s = String(v == null ? '' : v).replace(/[\r\n\t]/g, ' ');
    // il taglio si misura su quello che andra' nel file: 'CAFFE' con l'accento e
    // senza occupano lo stesso spazio, quindi l'accento non ruba un carattere
    while (adAscii(s).length > lunghezza) {
        s = s.slice(0, -1);
    }
    return s;
}

function ripulisciNumero(v, regola) {
    let s = String(v == null ? '' : v);
    const decimali = regola.decimali || 0;

    if (decimali === 0) {
        return s.replace(/\D/g, '').slice(0, regola.lunghezza);
    }

    // niente segno, niente lettere: restano solo cifre e separatori
    s = s.replace(/[^0-9.,]/g, '');

    // L'ULTIMO separatore e' quello dei decimali, gli altri sono delle migliaia e
    // si buttano. Senza questa regola, incollando '1.234,56' il primo punto veniva
    // preso per la virgola dei decimali e il risultato era '1,23': 1234,56 euro di
    // peso diventavano 1,23 senza un errore. E' la stessa trappola che c'era in
    // compose() lato server, ed e' l'unica strada con cui un separatore delle
    // migliaia puo' entrare in un campo (digitando non ci si arriva, il filtro
    // scatta a ogni tasto: si arriva incollando).
    const pezzi = s.split(/[.,]/);
    if (pezzi.length === 1) {
        return pezzi[0].slice(0, regola.intere);        // nessun separatore
    }
    const dec = pezzi.pop().slice(0, decimali);
    const intere = pezzi.join('').slice(0, regola.intere);
    return intere + ',' + dec;
}

export function ripulisci(campo, valore) {
    const r = regolaDi(campo);
    if (!r) return valore;
    return r.tipo === 'S' ? ripulisciNumero(valore, r) : ripulisciTesto(valore, r.lunghezza);
}

/**
 * A video la virgola, al server il punto.
 *
 * Il valore digitato finisce in tabella cosi' com'e' (RitiroService::insert lo
 * passa a insertGetId senza toccarlo) e solo dopo diventa file: se la colonna e'
 * numerica, MariaDB su '62,50' salva 62 e i decimali spariscono senza un errore.
 * Quindi al POST si manda il punto, esattamente come fa oggi type="number".
 *
 * @param  {Object} oggetto  i campi del form
 * @return {Object}          copia con i numerici normalizzati
 */
export function perServer(oggetto) {
    const out = Object.assign({}, oggetto);
    Object.keys(out).forEach(k => {
        const r = regolaDi(k);
        if (!r || r.tipo !== 'S' || !r.decimali) return;
        if (out[k] === null || out[k] === undefined || out[k] === '') return;
        out[k] = String(out[k]).replace(',', '.');
    });
    return out;
}

/**
 * Un numero MOSTRATO all'italiana: separatore delle migliaia col punto, decimali
 * con la virgola. 812.5 -> "812,50", 1234.5 -> "1.234,50".
 *
 * Solo per mostrare (elenchi, totali, valori calcolati). NON va usato per il valore
 * di un campo che l'utente modifica: li' il punto delle migliaia verrebbe preso
 * per un separatore dal filtro del tracciato. Per i campi c'e' perInput().
 *
 * Accetta anche stringhe con la virgola, e restituisce '' per un valore vuoto o
 * non numerico: meglio una cella vuota che "NaN".
 */
export function formatoIt(valore, decimali = 2) {
    if (valore === null || valore === undefined || valore === '') return '';
    const n = Number(String(valore).replace(',', '.'));
    if (!isFinite(n)) return '';
    // Non toLocaleString('it-IT'): per la regola italiana del CLDR il punto delle
    // migliaia non si mette sui numeri di quattro cifre (1234 resta 1234, 12345
    // diventa 12.345), e il risultato puo' cambiare da un browser all'altro.
    // Scritto a mano, esce uguale ovunque: 1.234,50.
    const [intera, dec] = Math.abs(n).toFixed(decimali).split('.');
    const conPunti = intera.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (n < 0 ? '-' : '') + conPunti + (dec ? ',' + dec : '');
}

/**
 * Un numero preparato per stare DENTRO un campo modificabile: virgola decimale,
 * niente separatore delle migliaia, niente zeri finali inutili.
 * 62.5 -> "62,5", "812.50" -> "812,5", 3 -> "3", null -> null.
 * Serve quando un valore arriva dal server (col punto) e va rimesso in un campo.
 */
export function perInput(valore) {
    if (valore === null || valore === undefined || valore === '') return null;
    const n = Number(String(valore).replace(',', '.'));
    if (!isFinite(n)) return null;
    return String(n).replace('.', ',');
}

/** Il valore di un campo con la virgola, come numero. Vuoto o non numerico = 0. */
export function daInput(valore) {
    const n = Number(String(valore === null || valore === undefined ? '' : valore).replace(',', '.'));
    return isFinite(n) ? n : 0;
}

// ------------------------------------------------------------ direttiva

/** maxlength, inputmode e suggerimento. Richiede la regola: se non c'e', si rifa' dopo. */
function decora(input, campo) {
    const r = regolaDi(campo);
    if (!r) return false;

    if (r.tipo === 'A') {
        input.setAttribute('maxlength', String(r.lunghezza));
    } else {
        input.removeAttribute('maxlength');
        // inputmode numerico: sulle tastiere dei telefoni evita di far comparire
        // lettere e segni che poi verrebbero comunque scartati
        input.setAttribute('inputmode', r.decimali > 0 ? 'decimal' : 'numeric');
    }
    input.setAttribute('title',
        r.tipo === 'S'
            ? (r.decimali > 0
                ? 'Numerico: max ' + r.intere + ' cifre intere e ' + r.decimali + ' decimali'
                : 'Numerico: max ' + r.lunghezza + ' cifre')
            : 'Testo: max ' + r.lunghezza + ' caratteri');
    return true;
}

function decoraTutti() {
    registrati.forEach((campo, input) => decora(input, campo));
}

function trovaInput(el) {
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
        ? el
        : el.querySelector('input, textarea');
}

function applica(el, campo) {
    const input = trovaInput(el);
    if (!input || !campo) return;

    input.setAttribute('data-tracciato', campo);
    registrati.set(input, campo);
    decora(input, campo);           // se le regole non ci sono ancora, ci pensa decoraTutti()

    if (input.__tracciatoAgganciato) return;
    input.__tracciatoAgganciato = true;

    // il filtro legge la regola AL MOMENTO della digitazione: se le regole non sono
    // ancora arrivate ripulisci() restituisce il valore intatto e il campo resta
    // libero come oggi, senza rompere niente
    input.addEventListener('input', function () {
        const nome = input.getAttribute('data-tracciato');
        const pulito = ripulisci(nome, input.value);
        if (pulito === input.value) return;
        // si rimette il cursore dove stava, altrimenti a ogni carattere scartato
        // salterebbe in fondo e scrivere in mezzo diventerebbe impossibile
        const posizione = Math.max(0, (input.selectionStart || 0) - (input.value.length - pulito.length));
        input.value = pulito;
        input.dispatchEvent(new Event('input', { bubbles: false }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        try { input.setSelectionRange(posizione, posizione); } catch (e) { /* input type date */ }
    });
}

function dimentica(el) {
    const input = trovaInput(el);
    if (input) registrati.delete(input);
}

Vue.directive('tracciato', {
    inserted(el, binding) { applica(el, binding.value); },
    componentUpdated(el, binding) { applica(el, binding.value); },
    unbind(el) { dimentica(el); }
});

export default {
    methods: {
        /** Da chiamare una volta nella pagina, es. in created(). */
        async caricaRegoleTracciato(tracciato) {
            return caricaRegole(this.$store.getters.uri, tracciato);
        },
        regolaTracciato(campo) { return regolaDi(campo); },
        ripulisciTracciato(campo, valore) { return ripulisci(campo, valore); },
        /** Da usare sull'oggetto appena prima del POST. */
        valoriPerTracciato(oggetto) { return perServer(oggetto); },
        /** Mostrare un numero all'italiana: 1.234,50 */
        formatoIt(valore, decimali = 2) { return formatoIt(valore, decimali); },
        /** Rimettere in un campo un numero arrivato dal server: "62,5" */
        perInput(valore) { return perInput(valore); },
        /** Leggere come numero un campo con la virgola: "62,5" -> 62.5 */
        daInput(valore) { return daInput(valore); },

        /**
         * Porta il cursore sul PRIMO campo rosso della pagina, nell'ordine in cui i
         * campi compaiono, e lo fa scorrere in vista.
         *
         * Cerca il colore e non i nomi dei campi: va dove l'utente vede l'errore,
         * qualunque sia il motivo per cui il campo e' rosso. Un campo rosso e':
         *   - un input/select/textarea con la classe bg-red-400
         *   - il primo input dentro un riquadro rosso (bg-red-400 o border-red-400),
         *     come i dettagli del ritiro o le righe dei bancali
         * querySelector con un elenco di selettori restituisce il primo elemento
         * nell'ordine del documento, cioe' il primo dall'alto.
         *
         * Aspetta il $nextTick: va chiamata subito dopo aver acceso l'evidenziazione
         * (resend / inviato = true), quando i campi non sono ancora rossi.
         */
        vaiAlPrimoRosso(radice) {
            this.$nextTick(() => {
                const base = radice || this.$el;
                if (!base || !base.querySelector) return;
                const el = base.querySelector(
                    'input.bg-red-400, select.bg-red-400, textarea.bg-red-400, ' +
                    '.bg-red-400 input, .border-red-400 input');
                if (!el) return;
                try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* vecchi browser */ }
                try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
            });
        }
    }
};
